import {dataURItoBlob} from "./common";

import {
  configInit,
  FULL_API_DOMAIN,
  ID_COOKIE_NAME,
  RECOMMENDATION_METHOD,
} from "../context/constants";
import {EN_STRING_MAP} from "../i18n/base";

const parseJSON = (newApi) => (response) => {
  if (
    response.status === 204 ||
    response.status === 205 ||
    response.redirected
  ) {
    return null;
  }
  return new Promise((resolve) => {
    response
      .json()
      .then((response) =>
        newApi ? resolve(response) : resolve(response.data)
      );
  });
};

const getLocation = (href) => {
  const l = document.createElement("a");
  l.href = href;
  return l;
};

const checkStatus = (response) => {
  if (response.status === undefined) {
    return response;
  }

  if (response.status >= 200 && response.status < 300 && !response.redirected) {
    return response;
  }

  if (response.redirected) {
    const url = getLocation(response.url);
    // history.push(`${url.pathname}${url.search}`);

    const error = new Error("Redirected");
    error.response = response;
    throw error;
  }

  throw response.data;

  // const error = new Error(response.data);
  // error.data = response;
  // throw error;
};

/** Device/session id for backend analytics — same query key as the host embed cookie (`rs_id`). */
const appendRsIdToUrl = (url) => {
  const id = configInit.rs_id;
  if (id != null && id !== "") {
    url.searchParams.set(ID_COOKIE_NAME, String(id));
  }
};

export const getRecommendedTryOnModels = (profile) => {
  const genderParam = profile.gender === "male" ? "men" : "women";
  const num = (v, fallback) => {
    if (v === "" || v === null || v === undefined) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const queryParams = {
    gender: genderParam,
    height: num(profile.height, 160),
    weight: num(profile.weight, 70),
    age: num(profile.age, 28),
    bustOffset: num(profile.bustOffset, 0),
    waistOffset: num(profile.waistOffset, 0),
    hipOffset: num(profile.hipOffset, 0),
  };

  const url = new URL(`${FULL_API_DOMAIN}/api/v1/try-on/models/recommend/`);
  Object.keys(queryParams).forEach((key) =>
    url.searchParams.append(key, queryParams[key])
  );
  appendRsIdToUrl(url);

  return fetch(url.href, {
    headers: {
      "Content-Type": "application/json",
      d: configInit.product.business.domain,
      p: configInit.product.id,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.matches)) return data.matches;
      if (Array.isArray(data?.data?.matches)) return data.data.matches;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.results)) return data.results;
      return [];
    })
    .catch(() => []);
};

export const getRecommendProducts = (profile) => {
  const queryParams = {
    product: configInit.product.id,
    gender: profile.gender,
    height: profile.height,
    weight: profile.weight,
    age: profile.age,
    bodyType: profile.bodyType || 0,
    bustOffset: profile.bustOffset || 0,
    waistOffset: profile.waistOffset || 0,
    hipOffset: profile.hipOffset || 0,
    fitPreference: profile.fitPreference,
    band: profile.band,
    cup: profile.cup,
    neck: profile.neck,
    length: profile.length,
    body_measurement:
      Object.keys(profile.bodyMeasurement).length > 0
        ? JSON.stringify(profile.bodyMeasurement)
        : null,
  };

  const options = Object.assign(
    {},
    { product: configInit.product.id },
    queryParams
  );
  const url = new URL(`${FULL_API_DOMAIN}/api/v1/widget/recommend/products`);
  Object.keys(options).forEach((key) =>
    url.searchParams.append(key, options[key])
  );
  appendRsIdToUrl(url);

  return fetch(url.href, {
    headers: {
      "Content-Type": "application/json",
      d: configInit.product.business.domain,
      p: configInit.product.id,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      return data;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * Maps try-on render API body to widget fields. Supports multi-angle `images`
 * as either:
 * - Legacy map: `{ front: "<base64>", ... }`
 * - Array: `[{ angle: "front", image: "<base64>" }, ...]`
 * Or legacy single `image_base64`.
 */
const normalizeTryOnRenderPayload = (body) => {
  if (!body || typeof body !== "object") return {};

  const mime = body.image_mime || body.mime || "image/png";

  const toDataUrl = (b64) =>
    typeof b64 === "string" && b64.length > 0
      ? `data:${mime};base64,${b64}`
      : null;

  const galleryFromImageMap = (imagesMap) => {
    const gallery = {};
    Object.keys(imagesMap).forEach((angle) => {
      const url = toDataUrl(imagesMap[angle]);
      if (url) gallery[angle] = url;
    });
    return gallery;
  };

  const galleryFromImageList = (imagesList) => {
    const gallery = {};
    if (!Array.isArray(imagesList)) return gallery;
    imagesList.forEach((item, index) => {
      if (!item || typeof item !== "object") return;
      const angle =
        typeof item.angle === "string" && item.angle.length > 0
          ? item.angle
          : `view_${index}`;
      const b64 = item.image ?? item.image_base64 ?? item.base64;
      const url = toDataUrl(b64);
      if (!url) return;
      const key =
        gallery[angle] !== undefined ? `${angle}_${index}` : angle;
      gallery[key] = url;
    });
    return gallery;
  };

  if (body.images != null) {
    let gallery = {};
    if (Array.isArray(body.images)) {
      gallery = galleryFromImageList(body.images);
    } else if (typeof body.images === "object") {
      gallery = galleryFromImageMap(body.images);
    }

    const out = {};
    if (Object.keys(gallery).length > 0) {
      out.try_on_gallery = gallery;
      if (body.errors && typeof body.errors === "object") {
        out.try_on_errors = body.errors;
      }
      const order = ["front", "side", "back"];
      const primary =
        order.map((k) => gallery[k]).find(Boolean) ||
        Object.values(gallery)[0];
      if (primary) {
        out.try_on_result = primary;
      }
    }
    return out;
  }

  const b64 = body.image_base64 || body.image || body.base64;
  if (b64) {
    return {
      try_on_result: `data:${mime};base64,${b64}`,
    };
  }

  return {};
};

const fetchWidgetRecommendation = (profileData) => {
  let option = {};
  const queryParams = {
    product: configInit.product.id,
    gender: profileData.gender,
    height: profileData.height,
    weight: profileData.weight,
    age: profileData.age,
    bodyType: profileData.bodyType || 0,
    bustOffset: profileData.bustOffset || 0,
    waistOffset: profileData.waistOffset || 0,
    hipOffset: profileData.hipOffset || 0,
    fitPreference: profileData.fitPreference,
    band: profileData.band,
    cup: profileData.cup,
    neck: profileData.neck,
    length: profileData.length,
  };

  if (profileData.face) {
    queryParams.face = JSON.stringify(profileData.face);
  }

  if (Object.keys(profileData.bodyMeasurement || {}).length > 0) {
    queryParams.body_measurement = JSON.stringify(profileData.bodyMeasurement);
  }

  if (
    configInit.product.business.config.interview.recommendation.method !==
    RECOMMENDATION_METHOD.MEASURES
  ) {
    option = {
      path: `api/v1/widget/recommend/sizes/`,
      method: "GET",
    };
  } else {
    option = {
      path: `api/v1/widget/recommend/measures/`,
      method: "GET",
    };
  }

  const url = new URL(`${FULL_API_DOMAIN}/${option.path}`);
  Object.keys(queryParams).forEach((key) =>
    url.searchParams.append(key, queryParams[key])
  );
  appendRsIdToUrl(url);

  return fetch(url, {
    method: option.method,
    headers: {
      d: configInit.product.business.domain,
      p: configInit.product.id,
    },
  })
    .then((response) => response.json())
    .then(checkStatus)
    .then((payload) => {
      if (payload.data !== undefined) return payload.data;
      return payload;
    });
};

export const postToApi = ({ file, skipVirtualTryOnRender, ...data }) => {
  const mapTryOnGender = (g) => {
    if (g === "male") return "men";
    if (g === "female") return "women";
    return g;
  };

  const virtualTryOnSelected =
    !skipVirtualTryOnRender &&
    configInit.product.has_virtual_model &&
    (!!data.tryOn || !!file);

  // Virtual try-on render (catalog_id or selfie) + same size/measures recommendation as non-VTO
  if (virtualTryOnSelected) {
    const formData = new FormData();
    formData.append("product", configInit.product.id);

    if (data.tryOn) {
      formData.append("catalog_id", data.tryOn);
    } else if (file) {
      const blob = dataURItoBlob(file);
      const selfieFile = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      formData.append("selfie", selfieFile);
    }

    formData.append("gender", mapTryOnGender(data.gender));
    formData.append("height", data.height ?? "");
    formData.append("weight", data.weight ?? "");
    formData.append("age", data.age ?? "");
    formData.append("bustOffset", data.bustOffset || 0);
    formData.append("waistOffset", data.waistOffset || 0);
    formData.append("hipOffset", data.hipOffset || 0);

    const renderUrl = new URL(`${FULL_API_DOMAIN}/api/v1/try-on/render/`);
    appendRsIdToUrl(renderUrl);
    const renderPromise = fetch(renderUrl, {
      method: "POST",
      headers: {
        d: configInit.product.business.domain,
        p: configInit.product.id,
      },
      body: formData,
    })
      .then((response) => response.json())
      .then(checkStatus)
      .then((payload) => {
        const body = payload?.data ?? payload;
        return normalizeTryOnRenderPayload(body);
      });

    return Promise.all([
      renderPromise.catch(() => ({})),
      fetchWidgetRecommendation(data).catch(() => ({})),
    ]).then(([tryPart, recPart]) => ({
      ...recPart,
      ...tryPart,
    }));
  }

  return fetchWidgetRecommendation(data).catch((err) => {
    throw err;
  });
};

export const quickPostToApi = (profile) => {
  return postToApi({
    ...profile,
    file: null,
    skipVirtualTryOnRender: true,
  });
};

export const postActivity = (action, id) => {
  const urlParams = new URLSearchParams(window.location.search);

  const queryParams = {
    product: configInit.product.id,
    _d: urlParams.get("domain"),
    _p: urlParams.get("path"),
  };
  const url = new URL(
    `${FULL_API_DOMAIN}/api/v1/widget/analytics/record/?event=${action}&business=${urlParams.get("domain")}`
  );
  Object.keys(queryParams).forEach((key) =>
    url.searchParams.append(key, queryParams[key])
  );
  appendRsIdToUrl(url);

  return fetch(url.href, {
    method: "POST",

    headers: {
      "Content-Type": "application/json",
      d: configInit.product.business.domain,
      p: configInit.product.id,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      return data;
    })
    .catch((err) => {
      throw err;
    });
};


export const getSetting = (d, p, lang) => {
  const url = new URL(`${FULL_API_DOMAIN}/api/v1/product/get/details/`);
  if (configInit.store_id) {
    url.searchParams.append("store_id", configInit.store_id);
  }
  if (lang) {
    url.searchParams.append("lang", lang);
  }

  appendRsIdToUrl(url);

  return fetch(url, {
    headers: {
      "Content-Type": "application/json",
      d: d,
      p: p,
    },
  })
    .then(checkStatus)
    .then(parseJSON(checkStatus))
    .then((res) => {
      const newString = { ...EN_STRING_MAP };

      if (
        res.data.product.business.config?.strings === null ||
        res.data.product.business.config?.strings === undefined
      ) {
        res.data.product.business.config.strings = {};
      }

      if (Object.keys(res.data.product.business.config?.strings).length === 0) {
        res.data.product.business.config.strings = newString;
      }

      return res;
    });
};
